import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** What broke, for the message + console triage ("the map", "the app"). */
  label: string;
  /** Compact fallback (used around the Canvas so the sidebar/panel survive). */
  inline?: boolean;
}

interface State {
  error: Error | null;
}

// Public-rollout guardrail: without a boundary, any throw in the layer
// builders (heavy geometry math on live data) blanks the entire app.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // console triage: what broke + where; state snapshot helps repro reports
    console.error(`[ErrorBoundary:${this.props.label}]`, error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 10, height: "100%", minHeight: this.props.inline ? 200 : "100vh",
          background: "#0a0e17", color: "rgba(255,255,255,0.85)",
          font: "400 14px Inter, sans-serif", padding: 24, textAlign: "center",
        }}
      >
        <div style={{ fontSize: 36 }}>🌀</div>
        <b>Something broke in {this.props.label}.</b>
        <p style={{ color: "rgba(255,255,255,0.55)", maxWidth: 420, margin: 0 }}>
          The error has been logged to the console. Reloading usually fixes it —
          your storm selection and view are not saved, so nothing is lost.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 6, padding: "9px 18px", borderRadius: 8, cursor: "pointer",
            border: "1px solid #60A5FA", background: "rgba(96,165,250,0.2)",
            color: "white", font: "600 13px Inter, sans-serif",
          }}
        >
          ↻ Reload
        </button>
      </div>
    );
  }
}

/** MapLibre + deck.gl both require WebGL — locked-down/older devices otherwise
 *  get a silent black rectangle. Checked once before mounting the app. */
export function webglAvailable(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}
